import {
  Alert,
  Anchor,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconFileText,
  IconInfoCircle,
  IconListCheck,
  IconRefresh,
  IconSchool,
  IconTrash,
} from "@tabler/icons-react";
import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getAssignmentPageBySlug } from "../../../data/assignmentPages";
import Seo from "../../../seo/Seo";
import {
  buildCanonicalUrl,
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
} from "../../../seo/utils";
import { getAssignmentPagePath } from "../../../utils/routes";
import AssignmentFAQ from "../AssignmentFAQ";
import "./MlaHeadingFormatPage.css";

type MlaGeneratorState = {
  fullName: string;
  instructorName: string;
  courseName: string;
  submissionDate: string;
  paperTitle: string;
  headerLastName: string;
};

const PAGE_SLUG = "mla-heading-format";
const PAGE_PATH = getAssignmentPagePath(PAGE_SLUG);

const exampleState: MlaGeneratorState = {
  fullName: "Alex Johnson",
  instructorName: "Professor Morgan",
  courseName: "English 101",
  submissionDate: "5 June 2026",
  paperTitle: "The Role of Memory in Modern Literature",
  headerLastName: "Johnson",
};

const emptyState: MlaGeneratorState = {
  fullName: "",
  instructorName: "",
  courseName: "",
  submissionDate: "",
  paperTitle: "",
  headerLastName: "",
};

const formFields: Array<{
  key: keyof MlaGeneratorState;
  label: string;
  placeholder: string;
}> = [
  {
    key: "fullName",
    label: "Full name",
    placeholder: "Alex Johnson",
  },
  {
    key: "headerLastName",
    label: "Last name for header",
    placeholder: "Johnson",
  },
  {
    key: "instructorName",
    label: "Instructor name",
    placeholder: "Professor Morgan",
  },
  {
    key: "courseName",
    label: "Course name",
    placeholder: "English 101",
  },
  {
    key: "submissionDate",
    label: "Date",
    placeholder: "5 June 2026",
  },
  {
    key: "paperTitle",
    label: "Paper title",
    placeholder: "The Role of Memory in Modern Literature",
  },
];

const googleDocsSteps = [
  "Open a blank document and set the font to Times New Roman, 12 pt.",
  "Go to Format, Line and paragraph spacing, and choose Double.",
  "Type your MLA heading on the first page, left aligned.",
  "Use Insert, Headers and footers, Header, then right align the header text.",
  "Choose Insert, Page numbers, and place your last name before the page number.",
];

const wordSteps = [
  "Open a blank document and use Times New Roman, 12 pt.",
  "Go to Layout and check that margins are set to 1 inch.",
  "Set paragraph spacing to double spacing before you type the heading.",
  "Type the MLA heading on the first page, left aligned.",
  "Open the header area, right align it, then add your last name and page number.",
];

const commonMistakes = [
  "Putting the student name and course details inside the running header.",
  "Adding the MLA heading to every page instead of only the first page.",
  "Forgetting the last name and page number in the top-right header.",
  "Centering or bolding the MLA heading lines.",
  "Using single spacing when the paper should be double spaced.",
  "Styling the title with bold, underline, or extra quotation marks.",
  "Writing the date in an inconsistent or unclear format.",
  "Leaving extra blank lines between the heading and the title.",
];

const relatedResources = [
  {
    title: "College Essay Editor",
    path: "/tools/college-essay-editor",
    description:
      "Review structure, readability, tone, and editing priorities before you submit a draft.",
  },
  {
    title: "Word Counter",
    path: "/tools/word-counter",
    description:
      "Count words, characters, paragraphs, and reading time for essays and assignments.",
  },
  {
    title: "Grammar Checker",
    path: "/tools/grammar-checker",
    description:
      "Check grammar, punctuation, clarity, and sentence issues in student writing.",
  },
];

const fallbackFaqs = [
  {
    question: "What does this MLA heading generator do?",
    answer:
      "The MLA heading generator creates the first-page MLA heading, the running header format, and a simple first-page preview from the details you enter.",
  },
  {
    question: "Is the MLA heading the same as the MLA header?",
    answer:
      "No. The MLA heading is the student and class information on the first page. The MLA header is the last name and page number that appears in the top-right corner of each page.",
  },
  {
    question: "Can I copy the generated MLA heading?",
    answer:
      "Yes. You can copy the heading, header, or full first-page preview and paste it into Google Docs or Microsoft Word.",
  },
  {
    question: "Should the MLA heading be double spaced?",
    answer:
      "Yes. The MLA heading should use the same double spacing as the rest of the paper.",
  },
];

function getValue(value: string, fallback: string) {
  return value.trim() || fallback;
}

function deriveLastName(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return parts[parts.length - 1] || "LastName";
}

function buildMlaOutput(state: MlaGeneratorState) {
  const fullName = getValue(state.fullName, "Your Full Name");
  const instructorName = getValue(state.instructorName, "Instructor Name");
  const courseName = getValue(state.courseName, "Course Name");
  const submissionDate = getValue(state.submissionDate, "Day Month Year");
  const paperTitle = getValue(state.paperTitle, "Your Paper Title");
  const headerLastName = getValue(
    state.headerLastName,
    deriveLastName(state.fullName),
  );
  const header = `${headerLastName} 1`;
  const headingLines = [fullName, instructorName, courseName, submissionDate];
  const heading = headingLines.join("\n");
  const firstPageText = [
    `Header: ${header}`,
    "",
    heading,
    "",
    paperTitle,
    "",
    "Start your essay here. Keep the document double spaced, use 1-inch margins, and keep the title in normal font unless your instructor says otherwise.",
  ].join("\n");

  return {
    fullName,
    instructorName,
    courseName,
    submissionDate,
    paperTitle,
    headerLastName,
    header,
    headingLines,
    heading,
    firstPageText,
  };
}

function fallbackCopyText(text: string) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "true");
  textArea.style.position = "absolute";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  fallbackCopyText(text);
}

function MlaHeadingFormatPage() {
  const { hash } = useLocation();
  const page = getAssignmentPageBySlug(PAGE_SLUG);
  const faqs = page?.faqs?.length ? page.faqs : fallbackFaqs;
  const [formState, setFormState] = useState<MlaGeneratorState>(exampleState);
  const [copiedKey, setCopiedKey] = useState("");

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(hash.replace(/^#/, ""));

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });

      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [hash]);

  const output = useMemo(() => buildMlaOutput(formState), [formState]);
  const canonicalUrl = buildCanonicalUrl(PAGE_PATH);
  const seoTitle =
    page?.titleTag ||
    page?.metaTitle ||
    "MLA Heading Generator | Free MLA Format Tool";
  const seoDescription =
    page?.metaDescription ||
    "Use the free MLA Heading Generator to create a correct MLA heading, MLA header, and first-page preview for essays and research papers.";

  function updateField(
    key: keyof MlaGeneratorState,
    event: ChangeEvent<HTMLInputElement>,
  ) {
    setFormState((current) => ({
      ...current,
      [key]: event.currentTarget.value,
    }));
  }

  async function handleCopy(text: string, key: string) {
    try {
      await copyText(text);
      setCopiedKey(key);
      window.setTimeout(() => {
        setCopiedKey((currentKey) => (currentKey === key ? "" : currentKey));
      }, 1600);
    } catch {
      setCopiedKey("");
    }
  }

  const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "MLA Heading Generator",
    applicationCategory: "EducationalApplication",
    operatingSystem: "Web",
    url: canonicalUrl,
    description:
      "A free MLA heading generator that creates a correct MLA heading, MLA header, and first-page preview for student papers.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    featureList: [
      "MLA heading generator",
      "MLA header format preview",
      "First-page MLA paper preview",
      "Copy MLA heading and header",
      "Google Docs and Microsoft Word instructions",
    ],
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Create an MLA Heading",
    description:
      "Create a correct MLA heading and MLA header for the first page of a student paper.",
    step: [
      "Enter your full name, instructor name, course name, and date.",
      "Enter your paper title and last name for the running header.",
      "Copy the generated MLA heading and place it at the top-left of page one.",
      "Copy the MLA header format and place it in the top-right header area.",
      "Keep the paper double spaced with the title centered below the heading.",
    ].map((text, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      text,
    })),
  };

  const educationalResourceSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalResource",
    name: "MLA Heading Format",
    url: canonicalUrl,
    educationalUse: "Academic writing format guidance",
    learningResourceType: "Student formatting tool",
    teaches: [
      "MLA heading format",
      "MLA header format",
      "MLA first page layout",
      "MLA title placement",
    ],
  };

  return (
    <Container fluid className="mla-tool-page" px={0}>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={PAGE_PATH}
        ogTitle="MLA Heading Generator"
        ogDescription="Generate a correct MLA heading, MLA header, and full first-page preview for essays, research papers, and class assignments."
        keywords={[
          "mla heading generator",
          "mla heading",
          "mla format heading",
          "mla header",
          "mla header format",
          "mla heading format",
          "mla first page format",
          "mla heading example",
          "mla format generator",
        ]}
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tools", path: "/tools/" },
            { name: "MLA Heading Generator", path: PAGE_PATH },
          ]),
          createFaqSchema(faqs),
          webApplicationSchema,
          howToSchema,
          educationalResourceSchema,
        ]}
      />

      <Box className="mla-hero">
        <Container size="xl">
          <Stack gap="xl">
            <Breadcrumbs separator="/">
              <Anchor component={Link} to="/">
                Home
              </Anchor>
              <Anchor component={Link} to="/tools/">
                Tools
              </Anchor>
              <Text>MLA Heading Generator</Text>
            </Breadcrumbs>

            <div className="mla-hero-copy">
              <Group gap="sm">
                <Badge color="teal" size="lg" radius="sm">
                  Free MLA Tool
                </Badge>
                <Badge color="blue" size="lg" radius="sm" variant="light">
                  Academic Writing
                </Badge>
              </Group>
              <Title order={1} className="mla-title">
                MLA Heading Generator
              </Title>
              <Text size="lg" c="dimmed" className="mla-hero-text">
                Enter your paper details to generate the correct MLA heading,
                MLA header format, and first-page preview instantly. Copy the
                result into Google Docs or Microsoft Word before you submit.
              </Text>
            </div>

            <div className="mla-generator-grid">
              <Paper
                withBorder
                radius="md"
                p="xl"
                className="mla-form-panel"
                component="section"
                aria-labelledby="mla-generator-heading"
              >
                <Stack gap="lg">
                  <Stack gap={6}>
                    <Group gap="sm">
                      <ThemeIcon color="teal" variant="light" radius="md">
                        <IconFileText size={20} />
                      </ThemeIcon>
                      <Title id="mla-generator-heading" order={2}>
                        Enter Your MLA Details
                      </Title>
                    </Group>
                    <Text c="dimmed">
                      Replace the example text with your assignment details.
                      The preview updates as you type.
                    </Text>
                  </Stack>

                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    {formFields.map((field) => (
                      <TextInput
                        key={field.key}
                        label={field.label}
                        placeholder={field.placeholder}
                        value={formState[field.key]}
                        onChange={(event) => updateField(field.key, event)}
                      />
                    ))}
                  </SimpleGrid>

                  <Group gap="md">
                    <Button
                      type="button"
                      radius="md"
                      color="teal"
                      leftSection={<IconRefresh size={18} />}
                      onClick={() => setFormState(exampleState)}
                    >
                      Use Example
                    </Button>
                    <Button
                      type="button"
                      radius="md"
                      color="gray"
                      variant="light"
                      leftSection={<IconTrash size={18} />}
                      onClick={() => {
                        setFormState(emptyState);
                        setCopiedKey("");
                      }}
                    >
                      Clear
                    </Button>
                  </Group>

                  <Alert
                    icon={<IconInfoCircle size={18} />}
                    color="blue"
                    variant="light"
                    radius="md"
                  >
                    In MLA style, the heading belongs on the first page. The
                    running header with your last name and page number belongs
                    in the page header area.
                  </Alert>
                </Stack>
              </Paper>

              <Paper
                withBorder
                radius="md"
                p="xl"
                className="mla-preview-panel"
                component="section"
                aria-labelledby="mla-preview-heading"
              >
                <Stack gap="lg">
                  <Group justify="space-between" align="flex-start" gap="md">
                    <Stack gap={4}>
                      <Title id="mla-preview-heading" order={2}>
                        Generated MLA Format
                      </Title>
                      <Text c="dimmed">
                        Copy the heading, header, or full first-page preview.
                      </Text>
                    </Stack>
                  </Group>

                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                    <OutputBlock
                      label="Correct MLA heading"
                      value={output.heading}
                      copyLabel="Copy Heading"
                      copied={copiedKey === "heading"}
                      onCopy={() => handleCopy(output.heading, "heading")}
                    />
                    <OutputBlock
                      label="MLA header format"
                      value={output.header}
                      copyLabel="Copy Header"
                      copied={copiedKey === "header"}
                      onCopy={() => handleCopy(output.header, "header")}
                    />
                  </SimpleGrid>

                  <Divider />

                  <Group justify="space-between" align="center" gap="md">
                    <Text fw={800}>Full first-page preview</Text>
                    <Button
                      type="button"
                      radius="md"
                      color="teal"
                      variant="light"
                      leftSection={
                        copiedKey === "first-page" ? (
                          <IconCheck size={18} />
                        ) : (
                          <IconCopy size={18} />
                        )
                      }
                      onClick={() =>
                        handleCopy(output.firstPageText, "first-page")
                      }
                    >
                      {copiedKey === "first-page"
                        ? "Copied"
                        : "Copy First Page"}
                    </Button>
                  </Group>

                  <div className="mla-page-preview" aria-label="MLA first page preview">
                    <div className="mla-page-running-header">{output.header}</div>
                    <div className="mla-page-heading">
                      {output.headingLines.map((line) => (
                        <div key={line}>{line}</div>
                      ))}
                    </div>
                    <div className="mla-page-title">{output.paperTitle}</div>
                    <p>
                      Start your essay here. Keep the paper double spaced, use
                      1-inch margins, and continue the running header on every
                      page.
                    </p>
                  </div>
                </Stack>
              </Paper>
            </div>
          </Stack>
        </Container>
      </Box>

      <Container size="xl" py={48}>
        <div className="mla-content-layout">
          <main className="mla-content-main">
            <SeoSection title="What Is MLA Heading Format?">
              <Text>
                MLA heading format is the set of four lines placed at the
                top-left of the first page of an MLA paper: full name,
                instructor name, course name, and date. The heading identifies
                your paper before the centered title and essay text begin.
              </Text>
              <Text>
                This MLA heading generator gives students a fast template for
                essays, research papers, literature reviews, response papers,
                and other humanities assignments that use MLA style.
              </Text>
            </SeoSection>

            <SeoSection title="MLA Heading vs MLA Header">
              <div className="mla-comparison-grid">
                <div>
                  <Text fw={900}>MLA heading</Text>
                  <Text c="dimmed">
                    Appears once on page one. It includes your full name,
                    instructor name, course name, and date.
                  </Text>
                </div>
                <div>
                  <Text fw={900}>MLA header</Text>
                  <Text c="dimmed">
                    Appears in the top-right page header. It includes your last
                    name and page number, such as Johnson 1.
                  </Text>
                </div>
              </div>
            </SeoSection>

            <SeoSection title="Google Docs and Word Instructions">
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                <InstructionCard
                  title="Google Docs"
                  steps={googleDocsSteps}
                  icon={<IconSchool size={20} />}
                />
                <InstructionCard
                  title="Microsoft Word"
                  steps={wordSteps}
                  icon={<IconFileText size={20} />}
                />
              </SimpleGrid>
            </SeoSection>

            <SeoSection title="Common MLA Heading Mistakes">
              <List
                spacing="sm"
                icon={
                  <ThemeIcon color="teal" size={22} radius="xl">
                    <IconCheck size={14} />
                  </ThemeIcon>
                }
              >
                {commonMistakes.map((mistake) => (
                  <List.Item key={mistake}>{mistake}</List.Item>
                ))}
              </List>
            </SeoSection>

            <SeoSection title="MLA Heading Format Checklist">
              <div className="mla-checklist-grid">
                {[
                  "Use the same readable font as the rest of the paper.",
                  "Keep every line double spaced.",
                  "Left align the heading.",
                  "Center the paper title below the heading.",
                  "Do not bold, underline, or italicize the title unless needed for another work title.",
                  "Add the last name and page number to the top-right header.",
                ].map((item) => (
                  <div key={item} className="mla-checklist-item">
                    <IconListCheck size={19} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </SeoSection>

            <SeoSection title="Related Academic Writing Resources">
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                {relatedResources.map((resource) => (
                  <Link
                    key={resource.path}
                    to={resource.path}
                    className="mla-related-card"
                  >
                    <Text fw={900}>{resource.title}</Text>
                    <Text size="sm" c="dimmed">
                      {resource.description}
                    </Text>
                  </Link>
                ))}
              </SimpleGrid>
            </SeoSection>

            <SeoSection title="MLA Heading Generator FAQs">
              <AssignmentFAQ faqs={faqs} />
            </SeoSection>
          </main>

          <aside className="mla-sidebar" aria-label="MLA formatting summary">
            <div className="mla-sidebar-sticky">
              <Paper withBorder radius="md" p="lg" className="mla-sidebar-card">
                <Stack gap="md">
                  <Group gap="sm">
                    <ThemeIcon color="blue" variant="light" radius="md">
                      <IconInfoCircle size={20} />
                    </ThemeIcon>
                    <Text fw={900}>Quick MLA Rules</Text>
                  </Group>
                  <ul className="mla-sidebar-list">
                    <li>Heading: first page only.</li>
                    <li>Header: every page.</li>
                    <li>Title: centered, normal font.</li>
                    <li>Spacing: double throughout.</li>
                    <li>Margins: usually 1 inch.</li>
                  </ul>
                  <Button
                    component={Link}
                    to="#mla-generator-heading"
                    color="teal"
                    radius="md"
                    variant="light"
                  >
                    Back to Generator
                  </Button>
                </Stack>
              </Paper>
            </div>
          </aside>
        </div>
      </Container>
    </Container>
  );
}

function OutputBlock({
  label,
  value,
  copyLabel,
  copied,
  onCopy,
}: {
  label: string;
  value: string;
  copyLabel: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="mla-output-block">
      <Group justify="space-between" align="center" gap="sm">
        <Text fw={800}>{label}</Text>
        <Button
          type="button"
          size="compact-sm"
          radius="md"
          variant="subtle"
          color="teal"
          leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
          onClick={onCopy}
        >
          {copied ? "Copied" : copyLabel}
        </Button>
      </Group>
      <pre>{value}</pre>
    </div>
  );
}

function SeoSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="mla-seo-section">
      <Stack gap="md">
        <Title order={2}>{title}</Title>
        {children}
      </Stack>
    </section>
  );
}

function InstructionCard({
  title,
  steps,
  icon,
}: {
  title: string;
  steps: string[];
  icon: ReactNode;
}) {
  return (
    <div className="mla-instruction-card">
      <Group gap="sm">
        <ThemeIcon color="blue" variant="light" radius="md">
          {icon}
        </ThemeIcon>
        <Text fw={900}>{title}</Text>
      </Group>
      <ol>
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </div>
  );
}

export default MlaHeadingFormatPage;
